// =====================================================
// IMPULSIVE TRADER SECTION - Journal Detail View
// Shows "If traded impulsively..." analysis for NO_TRADE entries
// =====================================================

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getImpulsiveOutcomeForEntry } from '@/services/impulsiveTraderService';
import type { ImpulsiveTraderOutcome } from '@/types/impulsiveTrader';

const { FiChevronDown, FiChevronUp, FiZap, FiTrendingDown, FiTrendingUp, FiShield, FiAlertTriangle } = FiIcons;

interface ImpulsiveTraderSectionProps {
  journalEntryId: string;
}

const ImpulsiveTraderSection: React.FC<ImpulsiveTraderSectionProps> = ({ journalEntryId }) => {
  const [outcome, setOutcome] = useState<ImpulsiveTraderOutcome | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const loadOutcome = async () => {
      setIsLoading(true);
      try {
        const data = await getImpulsiveOutcomeForEntry(journalEntryId);
        setOutcome(data);
      } catch (error) {
        console.error('Error loading impulsive trader outcome:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOutcome();
  }, [journalEntryId]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm p-3">
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent" />
        <span>Loading impulsive analysis...</span>
      </div>
    );
  }

  if (!outcome || outcome.hypotheticalRMultiple === null) {
    return null;
  }

  const isLossAvoided = outcome.capitalSaved;
  const hypotheticalPnL = outcome.hypotheticalRMultiple;

  return (
    <div className="rounded-xl overflow-hidden border border-amber-500/30">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 bg-amber-500/10 flex items-center justify-between hover:bg-amber-500/15 transition-colors"
      >
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiZap} className="w-5 h-5 text-amber-400" />
          <div className="text-left">
            <span className="text-sm font-semibold text-amber-400">
              If traded impulsively...
            </span>
            {!isExpanded && (
              <p className="text-xs text-gray-500 mt-0.5">
                {isLossAvoided 
                  ? `You avoided a ${Math.abs(hypotheticalPnL).toFixed(1)}R loss`
                  : hypotheticalPnL > 0 
                    ? `Missed ${hypotheticalPnL.toFixed(1)}R opportunity`
                    : 'Neutral outcome'
                }
              </p>
            )}
          </div>
        </div>
        <SafeIcon 
          icon={isExpanded ? FiChevronUp : FiChevronDown} 
          className="w-5 h-5 text-amber-400" 
        />
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-4 bg-gray-800/30 space-y-4">
              {/* Hypothetical Entry */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-gray-800/50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Hypothetical Entry</div>
                  <div className="text-sm font-bold text-white tabular-nums">
                    ${outcome.hypotheticalEntry.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
                
                {outcome.directionIfTraded && (
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Direction</div>
                    <div className={`text-sm font-bold flex items-center gap-1 ${
                      outcome.directionIfTraded === 'LONG' ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      <SafeIcon 
                        icon={outcome.directionIfTraded === 'LONG' ? FiTrendingUp : FiTrendingDown} 
                        className="w-4 h-4" 
                      />
                      {outcome.directionIfTraded}
                    </div>
                  </div>
                )}
                
                {outcome.hypotheticalStopLoss && (
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Stop Loss</div>
                    <div className="text-sm font-bold text-red-400 tabular-nums">
                      ${outcome.hypotheticalStopLoss.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                )}
              </div>

              {/* Worst Excursion */}
              {outcome.worstExcursion !== null && (
                <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-gray-400">Worst Excursion</span>
                  </div>
                  <span className="text-sm font-bold text-red-400">
                    ${Math.abs(outcome.worstExcursion).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}

              {/* 24h Later Price */}
              {outcome.price24hLater && (
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                  <span className="text-sm text-gray-400">Price 24h Later</span>
                  <span className={`text-sm font-bold tabular-nums ${
                    outcome.price24hLater > outcome.hypotheticalEntry ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    ${outcome.price24hLater.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}

              {/* Outcome Summary */}
              <div className={`p-4 rounded-lg border ${
                isLossAvoided 
                  ? 'bg-emerald-500/10 border-emerald-500/20' 
                  : hypotheticalPnL > 0
                    ? 'bg-amber-500/10 border-amber-500/20'
                    : 'bg-gray-800/50 border-white/5'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SafeIcon 
                      icon={isLossAvoided ? FiShield : FiZap} 
                      className={`w-5 h-5 ${isLossAvoided ? 'text-emerald-400' : 'text-amber-400'}`} 
                    />
                    <span className="text-sm font-semibold text-white">
                      {outcome.hypotheticalOutcome === 'WIN' && 'Would Have Won'}
                      {outcome.hypotheticalOutcome === 'LOSS' && 'Would Have Lost'}
                      {outcome.hypotheticalOutcome === 'BREAKEVEN' && 'Would Be Breakeven'}
                    </span>
                  </div>
                  <span className={`text-lg font-bold ${
                    hypotheticalPnL > 0 ? 'text-amber-400' : hypotheticalPnL < 0 ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    {hypotheticalPnL >= 0 ? '+' : ''}{hypotheticalPnL.toFixed(1)}R
                  </span>
                </div>
                
                <p className={`text-sm ${isLossAvoided ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {isLossAvoided && outcome.lossAvoided
                    ? `By NOT trading, you avoided a potential $${outcome.lossAvoided.toLocaleString()} loss. Discipline preserved your capital.`
                    : hypotheticalPnL > 0
                      ? `You missed a potential ${hypotheticalPnL.toFixed(1)}R gain. Remember: protecting capital consistently beats chasing occasional wins.`
                      : 'The trade would have been roughly breakeven. Your patience cost nothing.'
                  }
                </p>
              </div>

              {/* Philosophy Quote */}
              {isLossAvoided && (
                <div className="pt-2 text-center">
                  <p className="text-xs text-gray-500 italic">
                    "Discipline is not just risk control. It is alpha avoided."
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImpulsiveTraderSection;
